import { API_URL } from '@/config';
import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserPlus, Upload, Edit, Download, Key } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import axios from 'axios';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { getImageUrl } from '@/utils/imageUrl';

const API = `${API_URL}/api`;

const AdminMembersPage = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [formData, setFormData] = useState({
    user_id: '',
    name: '',
    email: '',
    mobile: '',
    role: 'Member',
    date_of_birth: '',
    address: '',
    spouse_name: '',
    spouse_mobile: '',
    spouse_date_of_birth: '',
    wedding_anniversary_date: '',
    professional_category: '',
    professional_title: '',
    company_name: '',
    office_address: '',
    photo_url: '',
    spouse_photo_url: '',
    joint_photo_url: '',
    business_card_url: ''
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => { loadMembers(); }, []);

  const loadMembers = async () => {
    try {
      const res = await axios.get(`${API}/members`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      setMembers(res.data);
    } catch (error) {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      user_id: '',
      name: '',
      email: '',
      mobile: '',
      role: 'Member',
      date_of_birth: '',
      address: '',
      spouse_name: '',
      spouse_mobile: '',
      spouse_date_of_birth: '',
      wedding_anniversary_date: '',
      professional_category: '',
      professional_title: '',
      company_name: '',
      office_address: '',
      photo_url: '',
      spouse_photo_url: '',
      joint_photo_url: '',
      business_card_url: ''
    });
    setEditMode(false);
  };

  const handleAddMember = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleEditMember = (member) => {
    setFormData({
      user_id: member.user_id,
      name: member.name || '',
      email: member.email || '',
      mobile: member.mobile || '',
      role: member.role || 'Member',
      date_of_birth: member.date_of_birth?.split('T')[0] || '',
      address: member.address || '',
      spouse_name: member.spouse_name || '',
      spouse_mobile: member.spouse_mobile || '',
      spouse_date_of_birth: member.spouse_date_of_birth?.split('T')[0] || '',
      wedding_anniversary_date: member.wedding_anniversary_date?.split('T')[0] || '',
      professional_category: member.professional_category || '',
      professional_title: member.professional_title || '',
      company_name: member.company_name || '',
      office_address: member.office_address || '',
      photo_url: member.photo_url || '',
      spouse_photo_url: member.spouse_photo_url || '',
      joint_photo_url: member.joint_photo_url || '',
      business_card_url: member.business_card_url || ''
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleMemberFileUpload = async (file, fieldName) => {
    if (!file) return;
    
    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, uploadFormData, {
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` },
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData(prev => ({ ...prev, [fieldName]: response.data.url }));
      toast.success('File uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await axios.put(`${API}/members/${formData.user_id}`, formData, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
        toast.success('Member updated successfully!');
      } else {
        const signupData = {
          email: formData.email,
          password: 'Change@123',
          name: formData.name,
          mobile: formData.mobile
        };
        const response = await axios.post(`${API}/auth/signup`, signupData, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
        
        if (formData.role !== 'Member') {
          await axios.put(`${API}/members/${response.data.user.user_id}/role?role=${formData.role}`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
        }
        
        const updateData = {
          date_of_birth: formData.date_of_birth,
          address: formData.address,
          spouse_name: formData.spouse_name,
          spouse_mobile: formData.spouse_mobile,
          spouse_date_of_birth: formData.spouse_date_of_birth,
          wedding_anniversary_date: formData.wedding_anniversary_date,
          professional_category: formData.professional_category,
          professional_title: formData.professional_title,
          company_name: formData.company_name,
          office_address: formData.office_address,
          photo_url: formData.photo_url,
          spouse_photo_url: formData.spouse_photo_url,
          joint_photo_url: formData.joint_photo_url,
          business_card_url: formData.business_card_url
        };
        await axios.put(`${API}/members/${response.data.user.user_id}`, updateData, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
        
        toast.success('Member added successfully! Default password: Change@123');
      }
      setDialogOpen(false);
      resetForm();
      loadMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save member');
    }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await axios.put(`${API}/members/${userId}/role?role=${role}`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      toast.success('Role updated');
      loadMembers();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleStatusToggle = async (userId, isActive) => {
    try {
      await axios.put(`${API}/members/${userId}/status?is_active=${!isActive}`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      toast.success('Status updated');
      loadMembers();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedMember) return;
    
    try {
      await axios.put(`${API}/members/${selectedMember.user_id}/password?new_password=Change@123`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      toast.success(`Password reset to "Change@123" for ${selectedMember.name}`);
      setPasswordDialogOpen(false);
      setSelectedMember(null);
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const openResetPasswordDialog = (member) => {
    setSelectedMember(member);
    setPasswordDialogOpen(true);
  };

  const downloadTemplate = () => {
    const template = [
      {
        'Name': 'John Doe',
        'Email': 'john@example.com',
        'Mobile': '9876543210',
        'Role': 'Member',
        'Date of Birth': '1985-05-15',
        'Professional Category': 'Business',
        'Professional Title': 'CEO',
        'Company Name': 'Example Corp',
        'Photo URL': 'https://example.com/photo.jpg'
      }
    ];
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Members Template');
    XLSX.writeFile(wb, 'members_template.xlsx');
    toast.success('Template downloaded!');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const workbook = XLSX.read(event.target.result, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const data = XLSX.utils.sheet_to_json(sheet);

          let successCount = 0;
          let failCount = 0;

          for (const row of data) {
            try {
              const signupData = {
                email: row['Email'] || row['email'],
                password: 'Change@123',
                name: row['Name'] || row['name'],
                mobile: (row['Mobile'] || row['mobile'] || '').toString()
              };

              const response = await axios.post(`${API}/auth/signup`, signupData, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
              
              const role = row['Role'] || row['role'] || 'Member';
              if (role !== 'Member') {
                await axios.put(`${API}/members/${response.data.user.user_id}/role?role=${role}`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
              }
              
              const updateData = {
                date_of_birth: row['Date of Birth'] || row['date_of_birth'] || null,
                professional_category: row['Professional Category'] || row['professional_category'] || null,
                professional_title: row['Professional Title'] || row['professional_title'] || null,
                company_name: row['Company Name'] || row['company_name'] || null,
                photo_url: row['Photo URL'] || row['photo_url'] || null
              };
              await axios.put(`${API}/members/${response.data.user.user_id}`, updateData, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
              
              successCount++;
            } catch (err) {
              console.error('Failed to import member:', err);
              failCount++;
            }
          }

          toast.success(`Imported ${successCount} members successfully! ${failCount} failed.`);
          setUploadDialogOpen(false);
          loadMembers();
        } catch (error) {
          toast.error('Failed to parse Excel file');
        }
      };
      reader.readAsBinaryString(file);
    } catch (error) {
      toast.error('Failed to upload file');
    }
  };

  const exportMembers = () => {
    const exportData = members.map(m => ({
      'Name': m.name,
      'Email': m.email,
      'Mobile': m.mobile,
      'Role': m.role,
      'Status': m.is_active ? 'Active' : 'Inactive',
      'Date of Birth': m.date_of_birth || '',
      'Professional Category': m.professional_category || '',
      'Professional Title': m.professional_title || '',
      'Company Name': m.company_name || ''
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Members');
    XLSX.writeFile(wb, `members_export_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Members exported!');
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-12 w-12 border-b-2 border-primary rounded-full"></div></div>;

  return (
    <div className="space-y-6" data-testid="admin-members-page">
      <div className="flex flex-wrap gap-3">
        <Button onClick={handleAddMember} style={{ backgroundColor: '#D4AF37', color: '#0F172A' }} data-testid="add-member-btn">
          <UserPlus className="w-4 h-4 mr-2" />
          Add Member
        </Button>
        
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" style={{ borderColor: '#D4AF37', color: '#0F172A' }} data-testid="bulk-upload-btn">
              <Upload className="w-4 h-4 mr-2" />
              Bulk Upload
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Bulk Upload Members</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">
                  Download the template, fill in member details, and upload the Excel file.
                </p>
                <Button onClick={downloadTemplate} variant="outline" className="w-full" data-testid="download-template-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Download Template
                </Button>
              </div>
              <div>
                <Label>Upload Excel File</Label>
                <Input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  data-testid="file-upload-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Default password for all members: Change@123
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        
        <Button onClick={exportMembers} variant="outline" data-testid="export-btn">
          <Download className="w-4 h-4 mr-2" />
          Export Excel
        </Button>
      </div>
      
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Edit Member' : 'Add New Member'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="member-name-input" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required disabled={editMode} data-testid="member-email-input" />
                </div>
                <div>
                  <Label>Mobile *</Label>
                  <Input value={formData.mobile} onChange={(e) => setFormData({...formData, mobile: e.target.value})} required data-testid="member-mobile-input" />
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                    <SelectTrigger data-testid="member-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Member">Member</SelectItem>
                      <SelectItem value="Treasurer">Treasurer</SelectItem>
                      <SelectItem value="Secretary">Secretary</SelectItem>
                      <SelectItem value="President">President</SelectItem>
                      <SelectItem value="Super Admin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date of Birth</Label>
                  <Input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})} data-testid="member-dob-input" />
                </div>
                <div>
                  <Label>Profile Photo</Label>
                  <Input type="file" accept="image/*" onChange={(e) => handleMemberFileUpload(e.target.files[0], 'photo_url')} disabled={uploading} data-testid="member-photo-upload" />
                  {formData.photo_url && <p className="text-xs text-muted-foreground mt-1">Uploaded</p>}
                </div>
                <div className="col-span-2">
                  <Label>Residential Address</Label>
                  <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="Full address" data-testid="member-address-input" />
                </div>
              </div>
            </div>
            
            <div className="space-y-4 border-t pt-4">
              <h3 className="text-lg font-semibold">Spouse Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Spouse Name</Label>
                  <Input value={formData.spouse_name} onChange={(e) => setFormData({...formData, spouse_name: e.target.value})} data-testid="spouse-name-input" />
                </div>
                <div>
                  <Label>Spouse Mobile</Label>
                  <Input value={formData.spouse_mobile} onChange={(e) => setFormData({...formData, spouse_mobile: e.target.value})} data-testid="spouse-mobile-input" />
                </div>
                <div>
                  <Label>Spouse Date of Birth</Label>
                  <Input type="date" value={formData.spouse_date_of_birth} onChange={(e) => setFormData({...formData, spouse_date_of_birth: e.target.value})} data-testid="spouse-dob-input" />
                </div>
                <div>
                  <Label>Wedding Anniversary</Label>
                  <Input type="date" value={formData.wedding_anniversary_date} onChange={(e) => setFormData({...formData, wedding_anniversary_date: e.target.value})} data-testid="anniversary-input" />
                </div>
                <div>
                  <Label>Spouse Photo</Label>
                  <Input type="file" accept="image/*" onChange={(e) => handleMemberFileUpload(e.target.files[0], 'spouse_photo_url')} disabled={uploading} data-testid="spouse-photo-upload" />
                  {formData.spouse_photo_url && <p className="text-xs text-muted-foreground mt-1">Uploaded</p>}
                </div>
                <div>
                  <Label>Joint Photo</Label>
                  <Input type="file" accept="image/*" onChange={(e) => handleMemberFileUpload(e.target.files[0], 'joint_photo_url')} disabled={uploading} data-testid="joint-photo-upload" />
                  {formData.joint_photo_url && <p className="text-xs text-muted-foreground mt-1">Uploaded</p>}
                </div>
              </div>
            </div>
            
            <div className="space-y-4 border-t pt-4">
              <h3 className="text-lg font-semibold">Professional Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Professional Category</Label>
                  <Input value={formData.professional_category} onChange={(e) => setFormData({...formData, professional_category: e.target.value})} data-testid="member-category-input" />
                </div>
                <div>
                  <Label>Professional Title</Label>
                  <Input value={formData.professional_title} onChange={(e) => setFormData({...formData, professional_title: e.target.value})} data-testid="member-title-input" />
                </div>
                <div className="col-span-2">
                  <Label>Company Name</Label>
                  <Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} data-testid="member-company-input" />
                </div>
                <div className="col-span-2">
                  <Label>Office/Business Address</Label>
                  <Input value={formData.office_address} onChange={(e) => setFormData({...formData, office_address: e.target.value})} placeholder="Office address" data-testid="member-office-address-input" />
                </div>
                <div className="col-span-2">
                  <Label>Business Card</Label>
                  <Input type="file" accept="image/*,.pdf" onChange={(e) => handleMemberFileUpload(e.target.files[0], 'business_card_url')} disabled={uploading} data-testid="business-card-upload" />
                  {formData.business_card_url && <p className="text-xs text-muted-foreground mt-1">Uploaded</p>}
                </div>
              </div>
            </div>
            
            {!editMode && (
              <p className="text-sm text-muted-foreground">
                Default password will be set to: <strong>Change@123</strong>
              </p>
            )}
            <Button type="submit" className="w-full" style={{ backgroundColor: '#D4AF37', color: '#0F172A' }} disabled={uploading} data-testid="submit-member-btn">
              {uploading ? 'Uploading...' : (editMode ? 'Update Member' : 'Add Member')}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Mobile</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.map((member) => (
              <TableRow key={member.user_id} data-testid={`member-row-${member.user_id}`}>
                <TableCell className="font-medium">{member.name}</TableCell>
                <TableCell>{member.email}</TableCell>
                <TableCell>{member.mobile}</TableCell>
                <TableCell>
                  <Select value={member.role} onValueChange={(v) => handleRoleChange(member.user_id, v)}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Member">Member</SelectItem>
                      <SelectItem value="Treasurer">Treasurer</SelectItem>
                      <SelectItem value="Secretary">Secretary</SelectItem>
                      <SelectItem value="President">President</SelectItem>
                      <SelectItem value="Super Admin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${member.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {member.is_active ? 'Active' : 'Inactive'}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => handleEditMember(member)} title="Edit Member" data-testid={`edit-member-${member.user_id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openResetPasswordDialog(member)} title="Reset Password" data-testid={`reset-password-${member.user_id}`} style={{ borderColor: '#D4AF37', color: '#D4AF37' }}>
                      <Key className="w-4 h-4 mr-1" />
                      Reset
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleStatusToggle(member.user_id, member.is_active)}>
                      {member.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      
      {/* Reset Password Confirmation Dialog */}
      <AlertDialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Password</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reset the password for <strong>{selectedMember?.name}</strong>?
              <br /><br />
              The new password will be set to: <strong className="text-primary">Change@123</strong>
              <br /><br />
              Please inform the member to change their password after logging in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedMember(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetPassword} style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}>
              Reset Password
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminMembersPage;
